// ============================================================
// NouvelleDemande — repartir d'un devis verrouillé pour une NOUVELLE affaire.
// ------------------------------------------------------------
// Philippe, 27 octobre 2026 : le 2026-10-024 était parti pour 1 pièce, le
// client en redemande 30. « Créer une révision » n'est pas le bon geste :
// elle suffixe la référence (-B) et corrige l'offre ENVOYÉE. Une nouvelle
// demande est une nouvelle affaire, avec son propre numéro.
//
// Le geste est celui de « Dupliquer » dans la liste — même route, même
// copie (un brouillon : gamme, consigne et brut repris ; ni plan, ni QR,
// ni référence interne, ni retours d'atelier) — mais là où on le cherche :
// sur le devis qu'on a sous les yeux. Et on OUVRE la copie, sinon il faut
// la retrouver dans une liste où elle ressemble à toutes les autres.
// ============================================================

import { useState } from 'react';
import { useNavigate } from 'react-router';
import Button from '../../ui/Button.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { api } from '../../lib/api.js';

/** Le libellé suit le nombre de pièces : on ne dit pas « cette pièce » de trois. */
export function libelleNouvelleDemande(nbPieces) {
  return nbPieces > 1 ? 'Nouvelle demande pour ces pièces' : 'Nouvelle demande pour cette pièce';
}

/**
 * Duplique le devis et rend ce qu'il faut pour ouvrir la copie. Pure à
 * l'appel près : `client` est le client API.
 */
export async function creerNouvelleDemande(client, devis) {
  const res = await client.post(`/devis/${devis.id}/duplication`);
  return {
    chemin: `/devis/${res.devis.id}`,
    message: `Devis ${res.devis.reference} créé depuis ${devis.reference}, en brouillon — `
      + 'changez la quantité. Les plans ne sont pas repris, ils restent sur l’original.',
  };
}

export default function NouvelleDemande({ devis, nbPieces }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [enCours, setEnCours] = useState(false);

  async function lancer() {
    setEnCours(true);
    try {
      const { chemin, message } = await creerNouvelleDemande(api, devis);
      toast.success(message);
      navigate(chemin);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Button size="sm" variant="primary" onClick={lancer} disabled={enCours}
      title="Un nouveau devis brouillon, avec son propre numéro, qui reprend ces pièces et leur gamme">
      {enCours ? 'Création…' : libelleNouvelleDemande(nbPieces)}
    </Button>
  );
}
